/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import handleFile from '../handleFile';
import { Dispatch, SetStateAction, useCallback, useRef, useState } from 'react';

/**
 * A custom hook for managing form state, validation errors, and submission handling.
 *
 * @param {Record<string, any>} defaults - The initial default values for the form fields.
 * @param {(fields: Record<string, any>, setErrors: Dispatch<SetStateAction<Record<string, any>>>) => Promise<void>} onSubmit - The function to handle form submission.
 * @param {(name: string, value: any) => Record<string, any>} [onChange] - Optional function to handle additional logic on field change.
 *
 * @returns {{
 *   fields: Record<string, any>,
 *   setFields: Dispatch<SetStateAction<Record<string, any>>>,
 *   errors: Record<string, any>,
 *   setErrors: Dispatch<SetStateAction<Record<string, any>>>,
 *   isSubmitting: boolean,
 *   setIsSubmitting: Dispatch<SetStateAction<boolean>>,
 *   handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
 *   handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>,
 *   handleReset: (key?: string, callback?: (key: string, setFields: Dispatch<SetStateAction<Record<string, any>>>, setErrors: Dispatch<SetStateAction<Record<string, any>>>) => void) => void,
 * }}
 * Returns the current form state, errors, submission status, and handler functions.
 */
const useForm = (
  defaults: Record<string, any>,
  onSubmit: (fields: Record<string, any>, setErrors: Dispatch<SetStateAction<Record<string, any>>>) => Promise<void>,
  handleChangeCallback?: (name: string, value: any, prev: Record<string, any>) => Record<string, any>,
) => {
  // Form fields state
  const [fields, setFields] = useState<Record<string, any>>(defaults);

  // Error messages state
  const [errors, setErrors] = useState<Record<string, any>>({ text: '', generic: '' });

  // Submission status state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Reference to initial defaults
  const defaultsRef = useRef<Record<string, any>>(defaults);

  // Handles changes in form fields and updates the fields state.
  const handleChange = useCallback(
    (e) => {
      // Extracting files, name, value, type, checked, and selectedOptions from the event target
      const files = handleFile(e);
      let { name, value, type, checked, selectedOptions } = e.currentTarget || e.target;
      console.log({ name, value, type, checked, selectedOptions });

      // If files are provided, set value to files
      if (Array.isArray(files) && files.length > 0) {
        value = files;
      }
      // If there are selected options (e.g., multi-select), convert them to an array of selected options
      else if (selectedOptions) {
        value = [];
        for (const selectedOption of selectedOptions) {
          value.push(selectedOption.value);
        }
      }
      // Otherwise, handle value conversion based on input type
      else {
        switch (type) {
          case 'number': // Handle number input
          case 'range': // Handle range input
            value = parseFloat(value); // Convert value to a number
            break;
          case 'checkbox': // Handle checkbox input
            value = checked; // Set value to checked state (true/false)
            break;
          default:
            break; // For other input types, the value property is usually sufficient
        }
      }

      // Update the fields state based on the new value
      setFields((prev) => {
        const typeofPrevValue = Array.isArray(prev[name]) ? 'array' : typeof prev[name];
        const typeofValue = Array.isArray(value) ? 'array' : typeof value;

        // Check if the type of previous value and the new value match
        if (value && typeofPrevValue !== typeofValue) {
          if (typeofValue === 'array') {
            if (value.length === 1) {
              if (typeofPrevValue === 'number' && Number(value[0])) {
                value = Number(value[0]);
              } else if (typeofPrevValue === typeof value[0]) {
                value = value[0];
              } else {
                console.error(
                  `Type mismatch! name: ${name} - ${typeofPrevValue} - ${prev[name]}, value: ${typeofValue} - ${value}`,
                );
                return prev;
              }
            } else {
              console.error(
                `Type mismatch! name: ${name} - ${typeofPrevValue} - ${prev[name]}, value: ${typeofValue} - ${value}`,
              );
              return prev;
            }
          } else if (typeofPrevValue === 'number' && Number(value)) {
            value = Number(value);
          } else if (typeofPrevValue === 'boolean') {
            if (typeofValue === 'string') {
              value = value === 'true';
            } else {
              value = Boolean(value);
            }
          } else {
            console.error(
              `Type mismatch! name: ${name} - ${typeofPrevValue} - ${prev[name]}, value: ${typeofValue} - ${value}`,
            );
            return prev;
          }
        }

        // If the value is an array, merge previous values with new values and remove duplicates
        if (typeofPrevValue === 'array') {
          const mergedValues = Array.from(
            new Set(
              [...prev[name], ...value].map((item) => {
                try {
                  return typeof item === 'object' ? JSON.stringify(item) : item;
                } catch (e) {
                  return item;
                }
              }),
            ),
          );

          value = [];
          for (const mergedValue of mergedValues) {
            if (!mergedValue) continue;
            try {
              const parseValue = JSON.parse(mergedValue).catch();
              if (Array.isArray(parseValue) && parseValue.length === 0) continue;
              if (!parseValue || Object.keys(parseValue).length === 0) continue;
              value.push(parseValue);
            } catch (e) {
              value.push(mergedValue);
            }
          }
        }

        // Call handleChangeCallback callback if provided and update state accordingly
        if (typeof handleChangeCallback === 'function') {
          const update = handleChangeCallback(name, value, prev);
          if (!update || typeof update !== 'object') return prev;
          console.log(update);
          return { ...prev, ...update };
        }

        console.log({ name, value });
        // Return the updated fields state
        return { ...prev, [name]: value };
      });
    },
    [handleChangeCallback],
  );

  // Handles form submission, invokes onSubmit, and manages errors.
  const handleSubmit = useCallback(
    async (e) => {
      try {
        e?.preventDefault();
        e?.stopPropagation();
        setErrors({ text: '', generic: '' });
        setIsSubmitting(true);
        await onSubmit(fields, setErrors);
      } catch (error) {
        console.error(`useForm.caught`, error.message, error.stack);
        setErrors((prev) => ({ ...prev, generic: error.message || 'Unable to process your request at this time.' }));
      } finally {
        setIsSubmitting(false);
      }
    },
    [fields, onSubmit],
  );

  /**
   * Resets form fields and errors to their initial/default state.
   * Optionally resets a single field if key is provided.
   */
  const handleReset = useCallback((key?: string) => {
    setFields((prev) => {
      if (!key) return { ...defaultsRef.current };
      return { ...prev, [key]: defaultsRef.current[key] || '' };
    });
    setErrors((prev) => {
      if (!key) return { text: '', generic: '' };
      return { ...prev, [key]: '' };
    });
  }, []);

  return {
    fields,
    errors,
    isSubmitting,
    setFields,
    handleChange,
    handleSubmit,
    handleReset,
  };
};

export default useForm;
